// Catalogo (tienda v5) — port de maquetas/tienda-v5-catalogo.html a JSX.
// Página 5 de Fase 3. Acento GOLD. CSS co-locado scopeado bajo .tv5-page-catalogo.
// Reusa los componentes shared (TopNav/Breadcrumb/Footer/BottomNav/MiniBar/CrossLinkBanner);
// solo aporta el CSS propio del grid/buscador/filtros.
//
// Sub-fase 4.0: datos REALES desde public.productos vía src/lib/tiendaApi (fetch cacheado,
// ~86 productos con fotos del bucket productos-fotos). Buscador + filtro por categoría (chips
// del enum real) + orden, todo client-side sobre la data del fetch. Loading = skeleton, error
// = mensaje + reintentar. Ruta a detalle por SKU (/producto/:sku).
//
// Ruta dev: /dev/tienda-v5-catalogo (la ruta prod /tienda/catalogo NO se toca hasta Fase 4).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TopNav, Breadcrumb, Footer, BottomNav, MiniBar, CrossLinkBanner, ScrollHintPill } from '../../components/tienda-v5';
import { TV5_ROUTES } from '../../components/tienda-v5/routes';
import { fetchProductosTienda, categoriasPresentes, categoriaEmoji, categoriaLabel, ordenCategoria } from '../../lib/tiendaApi';
import { useCart } from '../../lib/CartContext';

const fmt = (n) => '$' + Number(n).toLocaleString('es-UY');

// LimpackFlex — "llega hoy/mañana/el lunes" según el corte (16h lun-vie · sáb 10h · dom→lunes).
function cuandoFlex(d) {
  const day = d.getDay(), h = d.getHours() + d.getMinutes() / 60;
  if (day === 0) return { p: 'el lunes', hoy: false };
  if (day === 6) return h < 10 ? { p: 'hoy', hoy: true } : { p: 'el lunes', hoy: false };
  return h < 16 ? { p: 'hoy', hoy: true } : { p: 'mañana', hoy: false };
}
function flexBadge() { const c = cuandoFlex(new Date()); return c.hoy ? 'Hoy' : (c.p === 'mañana' ? 'Mañana' : 'Lun'); }

const ORDENES = [
  { key: 'popular', label: 'Ordenar por: Popular' },
  { key: 'precio-asc', label: 'Precio: menor a mayor' },
  { key: 'precio-desc', label: 'Precio: mayor a menor' },
  { key: 'descuento', label: 'Más descuento' },
];

const SKELETONS = Array.from({ length: 10 });

// Card de producto — la card entera navega al detalle; el control de cantidad no navega (stopPropagation).
// Feedback visual (Nico 4.6 + 4.x): con qty 0 muestra el "+" flotante; con qty>0 se transforma en un
// stepper [− N +] inline (mismo lenguaje visual del "+": pastilla blanca sobre la foto). El "−" decrementa
// con updateQty (a 0 el CartContext saca el ítem → vuelve el "+"); el "+" reusa onAdd (toast + bump del
// MiniBar). Borde teal en la card mientras hay cantidad. La qty sale del carrito real (reactiva, cross-tab).
function CardProducto({ p, onAdd }) {
  const { items, updateQty } = useCart();
  const qty = items.find((it) => it.sku === p.sku)?.cantidad ?? 0;
  const flex = cuandoFlex(new Date());
  const [pulse, setPulse] = useState(false);
  const pulseTimer = useRef(null);
  useEffect(() => () => clearTimeout(pulseTimer.current), []);

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); }; // corta navigate del <Link> + bubble
  function bump() {
    setPulse(true);
    clearTimeout(pulseTimer.current);
    pulseTimer.current = setTimeout(() => setPulse(false), 320);
  }
  // qty 0 → agrega (onAdd dispara toast + bump del MiniBar). El "+" del stepper hace lo mismo.
  function handleAdd(e) { stop(e); onAdd(p); bump(); }
  // "−": decremento absoluto; a 0 el CartContext saca el ítem y la card vuelve a mostrar solo el "+".
  function handleDec(e) { stop(e); updateQty(p.sku, qty - 1); bump(); }

  return (
    <Link
      to={`${TV5_ROUTES.producto}/${p.sku}`}
      className={'card-prod' + (qty > 0 ? ' en-carrito' : '')}
      aria-label={p.nombre}
    >
      <div className="card-prod-img">
        {p.agotado && <span className="badge-agotado">Agotado</span>}
        {p.imagen_url
          ? <img className="card-prod-foto" src={p.imagen_url} alt={p.nombre} loading="lazy" />
          : <span className="card-prod-emoji">{categoriaEmoji(p.categoria)}</span>}
        {qty > 0 ? (
          <div className="card-qty-stepper" onClick={stop} role="group" aria-label={`Cantidad de ${p.nombre}`}>
            <button
              type="button"
              className="card-qty-btn"
              onClick={handleDec}
              aria-label={qty === 1 ? `Quitar ${p.nombre} del carrito` : `Quitar uno de ${p.nombre}`}
            >−</button>
            <span className={'card-qty-val' + (pulse ? ' pulse' : '')} aria-live="polite">{qty}</span>
            <button
              type="button"
              className="card-qty-btn"
              onClick={handleAdd}
              aria-label={`Agregar otro ${p.nombre}`}
            >+</button>
          </div>
        ) : (
          <button
            type="button"
            className="btn-add-flotante"
            aria-label={`Agregar ${p.nombre} al carrito`}
            onClick={handleAdd}
          >+</button>
        )}
      </div>
      {(p.badge === 'top' || p.badge === 'nuevo') && (
        <div className={'card-prod-tag' + (p.badge === 'nuevo' ? ' nuevo' : '')}>{p.badge === 'top' ? '★ Top' : 'Nuevo'}</div>
      )}
      <div className="card-prod-nombre">{p.nombre}</div>
      <div className="card-prod-presentacion">{p.presentacion}</div>
      <div className="card-prod-precios">
        <span className="card-prod-precio">{fmt(p.precio)}</span>
        {p.tachado && <span className="card-prod-precio-tachado">{fmt(p.tachado)}</span>}
        {p.sale && <span className="card-prod-off">{p.sale.replace('-', '')} OFF</span>}
      </div>
      <span className={'card-prod-llega' + (flex.hoy ? '' : ' manana')}><span className="card-prod-llega-truck">🚚</span> Llega {flex.p}</span>
    </Link>
  );
}

export default function Catalogo() {
  const cuando = cuandoFlex(new Date());
  const [busqueda, setBusqueda] = useState('');
  const [categoria, setCategoria] = useState('todos');
  const [orden, setOrden] = useState('popular');

  const [productos, setProductos] = useState(null); // null = cargando
  const [error, setError] = useState(false);

  // FIX 5: al llegar con ?buscar=1 (desde el 🔍 del nav, incluido limpack.uy) → mostrar el buscador
  // (oculto en mobile ≤719px) + foco automático. Limpia el param para no re-disparar al refrescar.
  const buscadorRef = useRef(null);
  const [forzarBuscador, setForzarBuscador] = useState(false);
  useEffect(() => {
    let sp;
    try { sp = new URLSearchParams(window.location.search); } catch { return; }
    if (sp.get('buscar') === '1') {
      setForzarBuscador(true);
      setTimeout(() => { try { buscadorRef.current?.focus(); } catch { /* noop */ } }, 250);
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete('buscar');
        window.history.replaceState({}, '', u.pathname + u.search + u.hash);
      } catch { /* noop */ }
    }
    // Pre-seleccionar la categoría si llegás con ?cat=manos (desde el breadcrumb de un producto).
    const cat = sp.get('cat');
    if (cat) setCategoria(cat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargar = useCallback(() => {
    let alive = true;
    setError(false);
    setProductos(null);
    fetchProductosTienda()
      .then((list) => { if (alive) setProductos(list); })
      .catch((e) => { if (alive) { console.error('[Catalogo] fetch productos', e); setError(true); } });
    return () => { alive = false; };
  }, []);
  useEffect(() => cargar(), [cargar]);

  const loading = !error && productos == null;
  const categorias = useMemo(() => categoriasPresentes(productos || []), [productos]);

  const visibles = useMemo(() => {
    if (!productos) return [];
    const q = busqueda.trim().toLowerCase();
    const list = productos.filter((p) => {
      if (categoria !== 'todos' && p.categoria !== categoria) return false;
      if (!q) return true;
      return (
        p.nombre.toLowerCase().includes(q) ||
        (p.marca || '').toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q)
      );
    });
    const by = {
      'precio-asc': (a, b) => a.precio - b.precio,
      'precio-desc': (a, b) => b.precio - a.precio,
      'descuento': (a, b) => b.salePct - a.salePct,
    }[orden];
    return by ? [...list].sort(by) : list; // 'popular' = orden curado del fetch (destacado→orden→nombre)
  }, [productos, busqueda, categoria, orden]);

  // Fix orden visible (Sub-fase 4.6, opción X): en la vista "Todos" + orden Popular + sin búsqueda,
  // agrupamos por categoría con headers (cada categoría muestra TODOS sus productos, destacados incluidos).
  // Con categoría específica, búsqueda, u orden por precio/descuento → grid plano (el orden manda).
  const agrupar = categoria === 'todos' && orden === 'popular' && !busqueda.trim();
  const grupos = useMemo(() => {
    if (!agrupar) return null;
    // Cada categoría muestra TODOS sus productos (destacados incluidos). `visibles` ya viene ordenado
    // 'popular' (destacado→orden→nombre), así que dentro de cada categoría los ★ TOP quedan primero.
    // (Antes se sacaban los destacados a un grupo aparte, lo que dejaba categorías incompletas.)
    const byCat = new Map();
    for (const p of visibles) {
      if (!byCat.has(p.categoria)) byCat.set(p.categoria, []);
      byCat.get(p.categoria).push(p);
    }
    return [...byCat.entries()]
      .sort((a, b) => ordenCategoria(a[0]) - ordenCategoria(b[0]))
      .map(([key, items]) => ({ key, label: `${categoriaEmoji(key)} ${categoriaLabel(key)}`, items }));
  }, [agrupar, visibles]);

  // "+" de la card → agrega al carrito real (CartContext) + toast + bump del MiniBar.
  const { addItem } = useCart();
  const [toast, setToast] = useState('');
  const [bumpSignal, setBumpSignal] = useState(0);
  const toastTimer = useRef(null);
  const onAdd = useCallback((p) => {
    addItem(p, 1);
    setToast(`✓ ${p.nombre} agregado`);
    setBumpSignal((n) => n + 1);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 2000);
  }, [addItem]);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  return (
    <div className="tv5-page-catalogo">
      <style>{CATALOGO_CSS}</style>

      <TopNav active="tienda" accent="gold" />

      <Breadcrumb backHref={TV5_ROUTES.home} backLabel="Home" current="Tienda · Todos los productos" />

      <main>
        <section className="cat-hero">
          <span className="cat-flex-chip"><span className="cat-flex-bolt">⚡</span> LimpackFlex · Llega <b style={{ color: cuando.hoy ? '#10B981' : '#5BBDD8' }}>{cuando.p}</b></span>
          <h1>Todo lo que necesitás, sin salir de casa.</h1>
          <p>Productos elegidos uno por uno. Sin suscripción, sin planes.</p>
        </section>

        <div className="cat-franja-envio">
          ⚡ <span><strong>Gratis desde $1.000</strong> · Llega {cuando.p} en Montevideo y Canelones</span>
        </div>

        {/* BUSCADOR (oculto en mobile: vive en el 🔍 del nav — pero acá dejamos el campo visible en ≥720px) */}
        <div className={'cat-buscador-wrap' + (forzarBuscador ? ' forzado' : '')}>
          <div className="cat-buscador">
            <span className="cat-buscador-icon">🔍</span>
            <input
              ref={buscadorRef}
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre, marca o SKU…"
              aria-label="Buscar productos"
            />
          </div>
        </div>

        {error ? (
          <div className="cat-estado">
            <div className="cat-estado-emoji">😕</div>
            <p>No pudimos cargar el catálogo. Puede ser la conexión.</p>
            <button className="cat-vacio-reset" onClick={cargar}>Reintentar</button>
          </div>
        ) : loading ? (
          <section className="cat-grid-productos" aria-busy="true" aria-label="Cargando productos">
            {SKELETONS.map((_, i) => (
              <div className="card-prod card-prod-skel" key={i} aria-hidden="true">
                <div className="card-prod-img skel-box" />
                <div className="skel-line skel-line-lg" />
                <div className="skel-line skel-line-sm" />
                <div className="skel-line skel-line-md" />
              </div>
            ))}
          </section>
        ) : (
          <>
            {/* FILTROS chips (desktop) */}
            <div className="cat-filtros-chips" role="tablist" aria-label="Categorías">
              {categorias.map((c) => (
                <button
                  key={c.key}
                  role="tab"
                  aria-selected={categoria === c.key}
                  className={'chip-filtro' + (categoria === c.key ? ' activo' : '')}
                  onClick={() => setCategoria(c.key)}
                >
                  {c.emoji && `${c.emoji} `}{c.label} <span className="chip-filtro-count">{c.count}</span>
                </button>
              ))}
            </div>

            {/* FILTRO select (mobile) */}
            <div className="cat-filtros-select">
              <select value={categoria} onChange={(e) => setCategoria(e.target.value)} aria-label="Categoría">
                {categorias.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.key === 'todos' ? `Todas las categorías (${c.count})` : `${c.emoji} ${c.label} (${c.count})`}
                  </option>
                ))}
              </select>
            </div>

            {/* ORDEN + resultados */}
            <div className="cat-orden-row">
              <div className="cat-resultados-count">Mostrando <strong>{visibles.length} producto{visibles.length === 1 ? '' : 's'}</strong></div>
              <select className="cat-orden-sel" value={orden} onChange={(e) => setOrden(e.target.value)} aria-label="Ordenar">
                {ORDENES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </div>

            {/* GRID — agrupado por categoría en la vista "Todos" (opción X), plano si hay filtro/búsqueda/orden */}
            {visibles.length === 0 ? (
              <div className="cat-vacio">
                <div className="cat-vacio-emoji">🔍</div>
                <p>No encontramos productos con esos filtros.</p>
                <button className="cat-vacio-reset" onClick={() => { setBusqueda(''); setCategoria('todos'); }}>Limpiar filtros</button>
              </div>
            ) : grupos ? (
              grupos.map((g) => (
                <section className="cat-grupo" key={g.key}>
                  <h2 className="cat-grupo-h">{g.label} <span className="cat-grupo-count">{g.items.length}</span></h2>
                  <div className="cat-grid-productos">
                    {g.items.map((p) => <CardProducto key={p.sku} p={p} onAdd={onAdd} />)}
                  </div>
                </section>
              ))
            ) : (
              <section className="cat-grid-productos">
                {visibles.map((p) => <CardProducto key={p.sku} p={p} onAdd={onAdd} />)}
              </section>
            )}
          </>
        )}

        <div className="cat-crosslink-wrap">
          <CrossLinkBanner
            title="¿Todos los meses lo mismo?"
            sub="Suscribite a un kit y te olvidás. Pasamos por tu barrio con envío gratis, cada mes."
            ctaLabel="Ver los kits →"
            ctaHref={TV5_ROUTES.kits}
            accent="teal"
          />
        </div>
      </main>

      {/* Toast "✓ agregado" — feedback al tocar el "+" de una card (2s) */}
      <div className={'cat-toast' + (toast ? ' visible' : '')} role="status" aria-live="polite">{toast}</div>

      <ScrollHintPill />
      <MiniBar checkoutHref={TV5_ROUTES.checkout} bumpSignal={bumpSignal} />
      <Footer accent="gold" />
      <BottomNav active="tienda" />
    </div>
  );
}

const CATALOGO_CSS = `
.tv5-page-catalogo {
  --tv5cat-navy:#1A3461; --tv5cat-teal:#00A8B5; --tv5cat-gold:#C5A059; --tv5cat-green:#10B981;
  --tv5cat-bg:#0A1424; --tv5cat-card:rgba(255,255,255,0.04); --tv5cat-border:rgba(255,255,255,0.08);
  --tv5cat-text:#F8F6F1; --tv5cat-muted:rgba(248,246,241,0.65); --tv5cat-dim:rgba(248,246,241,0.45);
  --safe-bottom:env(safe-area-inset-bottom, 0px);
  background:var(--tv5cat-bg); color:var(--tv5cat-text); min-height:100vh; position:relative;
  overflow-x:clip; max-width:100%;
  font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;
  padding-bottom:calc(72px + var(--safe-bottom));
}
.tv5-page-catalogo::before {
  content:''; position:fixed; inset:0; pointer-events:none; z-index:0;
  background:
    radial-gradient(circle at 20% 10%, rgba(0,168,181,0.08) 0%, transparent 40%),
    radial-gradient(circle at 80% 60%, rgba(197,160,89,0.12) 0%, transparent 40%);
}
.tv5-page-catalogo main { position:relative; z-index:1; }
@media (min-width:900px){ .tv5-page-catalogo { padding-bottom:0; } }

/* HERO */
.tv5-page-catalogo .cat-hero { padding:20px 20px 8px; text-align:center; max-width:900px; margin:0 auto; }
.tv5-page-catalogo .cat-hero h1 {
  font-size:clamp(26px,6vw,44px); font-weight:800; letter-spacing:-0.03em; line-height:1.1; margin-bottom:10px;
  background:linear-gradient(135deg, var(--tv5cat-text) 0%, var(--tv5cat-gold) 100%);
  -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text;
}
.tv5-page-catalogo .cat-hero p { color:var(--tv5cat-muted); font-size:14px; max-width:520px; margin:0 auto; line-height:1.5; }

/* FRANJA envío */
.tv5-page-catalogo .cat-franja-envio {
  display:flex; align-items:center; gap:10px; padding:10px 14px; border-radius:10px; font-size:12px; font-weight:600;
  margin:20px auto 24px; max-width:800px;
  background:rgba(197,160,89,0.1); border:1px solid rgba(197,160,89,0.3); color:var(--tv5cat-gold);
}

/* BUSCADOR */
.tv5-page-catalogo .cat-buscador-wrap { max-width:800px; margin:0 auto; padding:0 20px 16px; }
/* FIX 5: ?buscar=1 fuerza mostrar el buscador aunque en mobile normalmente esté oculto */
.tv5-page-catalogo .cat-buscador-wrap.forzado { display:block !important; }
.tv5-page-catalogo .cat-buscador {
  display:flex; align-items:center; gap:10px;
  background:rgba(255,255,255,0.04); border:1px solid var(--tv5cat-border); border-radius:12px; padding:12px 16px;
}
.tv5-page-catalogo .cat-buscador input {
  flex:1; background:transparent; border:0; color:var(--tv5cat-text); font-size:16px; outline:none; /* 16px anti-zoom iOS */
}
.tv5-page-catalogo .cat-buscador input::placeholder { color:var(--tv5cat-dim); }
.tv5-page-catalogo .cat-buscador-icon { font-size:16px; color:var(--tv5cat-muted); }

/* FILTROS chips (desktop) */
.tv5-page-catalogo .cat-filtros-chips {
  max-width:1400px; margin:0 auto; padding:0 20px 12px;
  display:flex; gap:8px; overflow-x:auto; -webkit-overflow-scrolling:touch; scrollbar-width:none;
}
.tv5-page-catalogo .cat-filtros-chips::-webkit-scrollbar { display:none; }
.tv5-page-catalogo .chip-filtro {
  background:rgba(255,255,255,0.04); border:1px solid var(--tv5cat-border); color:var(--tv5cat-muted);
  padding:8px 14px; border-radius:999px; font-size:12px; font-weight:600; cursor:pointer; white-space:nowrap;
  display:inline-flex; align-items:center; gap:5px; flex-shrink:0; min-height:40px;
}
.tv5-page-catalogo .chip-filtro.activo { background:var(--tv5cat-gold); color:var(--tv5cat-navy); border-color:var(--tv5cat-gold); font-weight:800; }
.tv5-page-catalogo .chip-filtro-count { opacity:0.7; font-size:10px; }

/* FILTRO select (mobile) */
.tv5-page-catalogo .cat-filtros-select { display:none; }

/* ORDEN + resultados */
.tv5-page-catalogo .cat-orden-row {
  max-width:1400px; margin:0 auto; padding:12px 20px;
  display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap;
}
.tv5-page-catalogo .cat-resultados-count { font-size:12px; color:var(--tv5cat-muted); }
.tv5-page-catalogo .cat-resultados-count strong { color:var(--tv5cat-text); }
.tv5-page-catalogo .cat-orden-sel {
  background:rgba(255,255,255,0.04); border:1px solid var(--tv5cat-border); color:var(--tv5cat-text);
  padding:8px 12px; border-radius:8px; font-size:12px; font-family:inherit; cursor:pointer;
}

/* GRUPO por categoría (vista "Todos") — header + su propio grid */
.tv5-page-catalogo .cat-grupo { max-width:1400px; margin:0 auto; }
.tv5-page-catalogo .cat-grupo-h {
  display:flex; align-items:center; gap:8px; padding:20px 20px 8px;
  font-size:18px; font-weight:800; letter-spacing:-0.01em; color:var(--tv5cat-text);
}
.tv5-page-catalogo .cat-grupo-count {
  font-size:12px; font-weight:700; color:var(--tv5cat-dim);
  background:rgba(255,255,255,0.05); border:1px solid var(--tv5cat-border);
  padding:2px 9px; border-radius:999px;
}
.tv5-page-catalogo .cat-grupo .cat-grid-productos { padding-bottom:16px; }
.tv5-page-catalogo .cat-grupo:last-of-type .cat-grid-productos { padding-bottom:40px; }
@media (max-width:719px){ .tv5-page-catalogo .cat-grupo-h { font-size:16px; padding:16px 20px 6px; } }

/* GRID */
.tv5-page-catalogo .cat-grid-productos {
  max-width:1400px; margin:0 auto; padding:0 20px 40px;
  display:grid; grid-template-columns:repeat(2, 1fr); gap:10px;
}
@media (min-width:640px){ .tv5-page-catalogo .cat-grid-productos { grid-template-columns:repeat(3, 1fr); gap:14px; } }
@media (min-width:900px){ .tv5-page-catalogo .cat-grid-productos { grid-template-columns:repeat(4, 1fr); gap:16px; } }
@media (min-width:1200px){ .tv5-page-catalogo .cat-grid-productos { grid-template-columns:repeat(4, 1fr); } }

.tv5-page-catalogo .card-prod {
  background:var(--tv5cat-card); border:1px solid var(--tv5cat-border); border-radius:16px; padding:13px;
  position:relative; transition:all 0.3s; cursor:pointer; display:flex; flex-direction:column;
  text-decoration:none; color:inherit;
}
@media (hover:hover){
  .tv5-page-catalogo .card-prod:hover { transform:translateY(-3px); border-color:var(--tv5cat-gold); box-shadow:0 8px 24px rgba(197,160,89,0.15); }
}
.tv5-page-catalogo .card-prod-img {
  aspect-ratio:1; background:#fff;
  border-radius:12px; display:flex; align-items:center; justify-content:center;
  font-size:48px; margin-bottom:11px; position:relative; overflow:hidden;
}
.tv5-page-catalogo .card-prod-foto { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; display:block; }
.tv5-page-catalogo .card-prod-emoji { line-height:1; }
/* Mobile (≤639px, 2 columnas): imagen 3/4 (retrato) para que la foto se vea más grande en el celular. */
@media (max-width:639px){
  .tv5-page-catalogo .card-prod-img { aspect-ratio:3 / 4; margin-bottom:9px; }
  .tv5-page-catalogo .card-prod { padding:10px; }
}
.tv5-page-catalogo .badge-agotado {
  position:absolute; bottom:8px; left:8px; background:rgba(10,20,36,0.85); color:var(--tv5cat-dim);
  font-size:9px; font-weight:800; letter-spacing:0.06em; text-transform:uppercase;
  padding:3px 7px; border-radius:4px; z-index:2; border:1px solid var(--tv5cat-border);
}
.tv5-page-catalogo .badge-sale {
  position:absolute; top:8px; right:8px; background:#1A1A1A; color:#FFD700;
  font-size:10px; font-weight:900; letter-spacing:0.05em; padding:4px 8px; border-radius:4px;
  box-shadow:0 2px 8px rgba(0,0,0,0.3); z-index:2;
}
.tv5-page-catalogo .badge-sale::before { content:'SALE '; color:var(--tv5cat-text); font-weight:700; }
.tv5-page-catalogo .badge-nuevo, .tv5-page-catalogo .badge-top {
  position:absolute; top:34px; left:8px; font-size:9px; font-weight:800; letter-spacing:0.08em;
  padding:3px 7px; border-radius:4px; text-transform:uppercase; z-index:2;
}
@keyframes catFlash { 0%,100% { transform:scale(1); opacity:1; } 50% { transform:scale(1.06); opacity:0.82; } }
@keyframes catBlink { 0%,100% { opacity:1; } 50% { opacity:0.35; } }
@keyframes catGlow { 0%,100% { box-shadow:0 0 0 0 rgba(0,168,181,0); } 50% { box-shadow:0 0 14px 1px rgba(0,168,181,0.4); } }
.tv5-page-catalogo .badge-flex {
  position:absolute; top:8px; left:8px; z-index:3; color:#fff; font-size:9px; font-weight:900; letter-spacing:0.03em;
  padding:3px 7px; border-radius:999px; background:linear-gradient(135deg,var(--tv5cat-teal),#0090a0);
  box-shadow:0 3px 8px rgba(0,168,181,0.45); display:inline-flex; align-items:center; gap:3px;
  animation:catFlash 1.4s ease-in-out infinite;
}
.tv5-page-catalogo .badge-flex-bolt { display:inline-block; animation:catBlink 1s steps(1,end) infinite; }
.tv5-page-catalogo .cat-flex-chip {
  display:inline-flex; align-items:center; gap:6px; font-size:11px; font-weight:800; letter-spacing:0.1em;
  text-transform:uppercase; color:var(--tv5cat-teal); background:rgba(0,168,181,0.12);
  border:1px solid rgba(0,168,181,0.34); padding:6px 13px; border-radius:999px; margin-bottom:10px;
  animation:catGlow 2.2s ease-in-out infinite;
}
.tv5-page-catalogo .cat-flex-bolt { display:inline-block; animation:catBlink 1s steps(1,end) infinite; }
.tv5-page-catalogo .badge-nuevo { background:var(--tv5cat-teal); color:var(--tv5cat-navy); }
.tv5-page-catalogo .badge-top { background:var(--tv5cat-gold); color:var(--tv5cat-navy); }
.tv5-page-catalogo .btn-add-flotante {
  position:absolute; bottom:8px; right:8px; width:34px; height:34px; border-radius:50%;
  background:rgba(255,255,255,0.95); color:var(--tv5cat-navy); border:0; font-size:22px; font-weight:300;
  cursor:pointer; display:flex; align-items:center; justify-content:center; z-index:2; transition:all 0.2s;
  box-shadow:0 2px 8px rgba(0,0,0,0.15);
}
.tv5-page-catalogo .btn-add-flotante::after { content:''; position:absolute; inset:-5px; } /* tap 44px sin agrandar el círculo (34+10) */
@media (hover:hover){
  .tv5-page-catalogo .btn-add-flotante:hover { background:var(--tv5cat-gold); color:var(--tv5cat-navy); transform:scale(1.1); }
}
/* Stepper [− N +] — reemplaza al "+" flotante cuando hay cantidad en carrito. Mismo lenguaje visual
   (pastilla blanca sobre la foto, texto navy). Ancla abajo-derecha como el "+". */
.tv5-page-catalogo .card-qty-stepper {
  position:absolute; bottom:8px; right:8px; z-index:2;
  display:inline-flex; align-items:center; height:34px;
  background:rgba(255,255,255,0.95); border-radius:999px; overflow:hidden;
  box-shadow:0 2px 8px rgba(0,0,0,0.18);
}
.tv5-page-catalogo .card-qty-btn {
  width:30px; height:34px; border:0; background:transparent; color:var(--tv5cat-navy);
  font-size:20px; font-weight:400; line-height:1; cursor:pointer;
  display:flex; align-items:center; justify-content:center; -webkit-tap-highlight-color:transparent;
  transition:color 0.15s;
}
.tv5-page-catalogo .card-qty-btn:active { background:rgba(0,0,0,0.06); }
@media (hover:hover){ .tv5-page-catalogo .card-qty-btn:hover { color:var(--tv5cat-teal); } }
.tv5-page-catalogo .card-qty-val {
  min-width:20px; text-align:center; color:var(--tv5cat-navy);
  font-size:14px; font-weight:900; font-variant-numeric:tabular-nums;
}
/* Feedback al cambiar la cantidad: pulso corto sobre el número (0.32s) */
.tv5-page-catalogo .card-qty-val.pulse { animation:catQtyPulse 0.32s ease; }
@keyframes catQtyPulse { 0%{transform:scale(1)} 45%{transform:scale(1.3)} 100%{transform:scale(1)} }
/* Card con borde teal sutil si el producto está en el carrito */
.tv5-page-catalogo .card-prod.en-carrito { border-color:rgba(0,168,181,0.5); box-shadow:0 0 0 1px rgba(0,168,181,0.28); }
@media (hover:hover){ .tv5-page-catalogo .card-prod.en-carrito:hover { border-color:var(--tv5cat-teal); } }
.tv5-page-catalogo .card-prod-nombre {
  font-size:12px; font-weight:600; color:var(--tv5cat-text); margin-bottom:4px; line-height:1.3;
  display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; min-height:32px;
}
.tv5-page-catalogo .card-prod-presentacion { font-size:10px; color:var(--tv5cat-dim); margin-bottom:8px; }
.tv5-page-catalogo .card-prod-precios { display:flex; align-items:baseline; gap:6px; margin-top:auto; flex-wrap:wrap; }
.tv5-page-catalogo .card-prod-precio { font-size:19px; font-weight:800; color:var(--tv5cat-text); }
.tv5-page-catalogo .card-prod-precio-tachado { font-size:11px; color:var(--tv5cat-dim); text-decoration:line-through; }
.tv5-page-catalogo .card-prod-off { background:#10B981; color:#04120c; font-size:10.5px; font-weight:900; letter-spacing:0.01em; padding:2px 7px; border-radius:6px; align-self:center; white-space:nowrap; }
.tv5-page-catalogo .card-prod-tag { align-self:flex-start; font-size:9.5px; font-weight:800; letter-spacing:0.06em; text-transform:uppercase; color:var(--tv5cat-gold); margin-bottom:2px; }
.tv5-page-catalogo .card-prod-tag.nuevo { color:var(--tv5cat-teal); }
.tv5-page-catalogo .card-prod-llega { display:inline-flex; align-items:center; gap:5px; align-self:flex-start; margin-top:10px; background:transparent; color:#34D399; border:1px solid rgba(16,185,129,0.55); font-size:11px; font-weight:700; padding:4px 10px; border-radius:999px; white-space:nowrap; }
.tv5-page-catalogo .card-prod-llega.manana { color:#5BBDD8; border-color:rgba(91,189,216,0.55); }
.tv5-page-catalogo .card-prod-llega-truck { font-size:12px; }

/* ESTADO VACÍO (sin resultados de búsqueda/filtro — no existe en la maqueta, agregado para el caso real) */
.tv5-page-catalogo .cat-vacio { text-align:center; padding:48px 20px 60px; color:var(--tv5cat-muted); }
.tv5-page-catalogo .cat-vacio-emoji { font-size:44px; margin-bottom:12px; opacity:0.6; }
.tv5-page-catalogo .cat-vacio p { font-size:14px; margin-bottom:16px; }
.tv5-page-catalogo .cat-vacio-reset {
  background:rgba(197,160,89,0.15); border:1px solid rgba(197,160,89,0.4); color:var(--tv5cat-gold);
  padding:10px 20px; border-radius:999px; font-size:13px; font-weight:700; cursor:pointer; min-height:44px;
}

.tv5-page-catalogo .cat-crosslink-wrap { max-width:1200px; margin:40px auto 0; padding:0 20px; }

/* ESTADO ERROR de carga */
.tv5-page-catalogo .cat-estado { text-align:center; padding:48px 20px 60px; color:var(--tv5cat-muted); }
.tv5-page-catalogo .cat-estado-emoji { font-size:44px; margin-bottom:12px; opacity:0.7; }
.tv5-page-catalogo .cat-estado p { font-size:14px; margin-bottom:16px; }

/* SKELETON de carga (shimmer) */
.tv5-page-catalogo .card-prod-skel { pointer-events:none; }
.tv5-page-catalogo .skel-box, .tv5-page-catalogo .skel-line {
  background:linear-gradient(100deg, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.09) 50%, rgba(255,255,255,0.04) 70%);
  background-size:200% 100%; animation:catSkel 1.2s ease-in-out infinite; border-radius:8px;
}
.tv5-page-catalogo .skel-line { height:11px; margin-top:8px; }
.tv5-page-catalogo .skel-line-lg { width:90%; }
.tv5-page-catalogo .skel-line-sm { width:50%; }
.tv5-page-catalogo .skel-line-md { width:40%; height:16px; margin-top:10px; }
@keyframes catSkel { from { background-position:200% 0; } to { background-position:-200% 0; } }
@media (prefers-reduced-motion:reduce){ .tv5-page-catalogo .skel-box, .tv5-page-catalogo .skel-line { animation:none; } }

/* MOBILE compact (<720px): título chico, subtítulo fuera, buscador fuera (vive en el 🔍 del nav),
   chips → select, franja en 1 línea */
@media (max-width:719px){
  .tv5-page-catalogo .cat-hero { padding:12px 20px 4px; }
  .tv5-page-catalogo .cat-hero h1 { font-size:22px; line-height:1.15; margin-bottom:0; }
  .tv5-page-catalogo .cat-hero p { display:none; }
  .tv5-page-catalogo .cat-buscador-wrap { display:none; }
  .tv5-page-catalogo .cat-franja-envio { padding:7px 12px; font-size:11px; margin:10px 20px; border-radius:8px; }
  .tv5-page-catalogo .cat-filtros-chips { display:none; }
  .tv5-page-catalogo .cat-filtros-select { display:flex; gap:8px; padding:6px 20px 10px; align-items:stretch; }
  .tv5-page-catalogo .cat-filtros-select select {
    flex:1; min-width:0; background:rgba(255,255,255,0.05); border:1px solid var(--tv5cat-border);
    color:var(--tv5cat-text); padding:10px 32px 10px 12px; border-radius:10px; font-size:16px; font-weight:700; /* 16px anti-zoom iOS */
    -webkit-appearance:none; appearance:none;
    background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='%23F8F6F1'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
    background-repeat:no-repeat; background-position:right 12px center;
  }
  .tv5-page-catalogo .cat-orden-row { padding:4px 20px 12px; }
  .tv5-page-catalogo .cat-resultados-count { font-size:11px; color:var(--tv5cat-dim); }
  .tv5-page-catalogo .cat-orden-sel { font-size:16px; } /* 16px anti-zoom iOS */
}

/* Toast "✓ agregado" (mismo lenguaje visual que ProductoDetalle) */
.tv5-page-catalogo .cat-toast {
  position:fixed; bottom:calc(140px + var(--safe-bottom)); left:50%; transform:translateX(-50%) translateY(10px);
  background:rgba(16,185,129,0.96); color:#04361f; border-radius:999px;
  padding:12px 20px; font-size:13px; font-weight:800; z-index:130; max-width:calc(100% - 32px);
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
  box-shadow:0 16px 40px rgba(0,0,0,0.5); opacity:0; pointer-events:none;
  transition:opacity 0.25s, transform 0.25s;
}
.tv5-page-catalogo .cat-toast.visible { opacity:1; transform:translateX(-50%) translateY(0); }
@media (min-width:900px){ .tv5-page-catalogo .cat-toast { bottom:90px; } }

/* Reduced motion */
@media (prefers-reduced-motion:reduce){
  .tv5-page-catalogo .card-prod, .tv5-page-catalogo .btn-add-flotante { transition:none; }
  .tv5-page-catalogo .card-qty-val.pulse { animation:none; }
  .tv5-page-catalogo .cat-toast { transition:opacity 0.2s; }
}
`;
